import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import clsx from 'clsx'
import { BaseFieldProps } from './types'
import { useMemo, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import { IconCheckMarkOutline, IconCloseOutline, IconDownChevronOutline } from '@/components/icons'
import { DynamicIcon } from '@/components/icons/DynamicIcon'

interface SingleSelectComboboxFieldProps extends BaseFieldProps {
  applyButton?: React.ReactNode
  showIcons?: boolean
}

export const SingleSelectComboboxField = ({
  field,
  formFieldName,
  control,
  variant,
  applyButton,
  showIcons = false,
}: SingleSelectComboboxFieldProps) => {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Memoize the filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => {
    if (!field.choices?.length) return []

    // Only filter if there's a search query
    if (!searchQuery.trim()) return field.choices

    const lowerQuery = searchQuery.toLowerCase()
    return field.choices.filter((option) => {
      const label = typeof option === 'string' ? option : option.title
      return label.toLowerCase().includes(lowerQuery)
    })
  }, [field.choices, searchQuery])

  // Reference for the parent container of virtualized items
  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null)

  // Setup virtualization with fixed item height to prevent overlapping
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef,
    estimateSize: () => 36, // Fixed height for each item
    overscan: 5, // Number of items to render before/after the visible area
  })

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        const selectedValue = (formField.value as string) || ''

        // Get the display label for the selected value
        const getSelectedLabel = () => {
          if (!selectedValue || !field?.choices?.length) return ''

          const selectedOption = field.choices.find((option) => {
            const value = typeof option === 'string' ? option : option.value
            return value === selectedValue
          })

          return selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.title) : ''
        }

        // Define the handler for single selection
        const handleSelect = (optionValue: string) => {
          // If the same value is selected, clear the selection
          if (selectedValue === optionValue) {
            formField.onChange('')
          } else {
            // Otherwise set the new value
            formField.onChange(optionValue)
          }
          // Close the popover after selection
          setOpen(false)
        }

        return (
          <FormItem className={clsx('flex flex-1 flex-col', field.widget === 'hidden' && 'hidden')}>
            <FormLabel className={variant === 'row' ? 'sr-only' : 'block'}>{field.title}</FormLabel>

            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  className={cn('flex w-full items-center justify-between rounded-sm', {
                    'h-6 w-40': variant === 'row',
                  })}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={field.readonly}
                >
                  <div className="flex-1 truncate text-left">
                    {selectedValue ? (
                      showIcons ? (
                        <div className="inline-flex items-center">
                          <DynamicIcon size={20} className="mr-3" iconName={formField.value} lazyLoad={false} />
                          {getSelectedLabel()}
                        </div>
                      ) : (
                        getSelectedLabel()
                      )
                    ) : (
                      <span className="italic text-muted-foreground">{`Select ${field.title.toLowerCase()}`}</span>
                    )}
                  </div>
                  <IconDownChevronOutline className="size-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={`Search ${field.title.toLowerCase()}...`}
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>No options found.</CommandEmpty>
                    <CommandGroup>
                      {/* Virtualized list container */}
                      <div
                        ref={setParentRef}
                        className="max-h-[200px] w-full overflow-auto"
                        style={{
                          height: Math.min(filteredOptions.length * 36, 200),
                          width: '100%',
                        }}
                      >
                        {/* Set the total height of the virtualizer */}
                        <div
                          style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                          }}
                        >
                          {/* Only render visible items */}
                          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const option = filteredOptions[virtualRow.index]
                            if (!option) return null // Safety check

                            const optionValue = typeof option === 'string' ? option : option.value
                            const optionLabel = typeof option === 'string' ? option : option.title
                            const isSelected = selectedValue === optionValue

                            return (
                              <TooltipProvider key={virtualRow.key}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      data-index={virtualRow.index}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: `${virtualRow.size}px`,
                                        transform: `translateY(${virtualRow.start}px)`,
                                      }}
                                    >
                                      <CommandItem
                                        value={optionValue}
                                        onSelect={() => handleSelect(optionValue)}
                                        className="h-9"
                                      >
                                        {!showIcons && (
                                          <IconCheckMarkOutline
                                            className={clsx('mr-4 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                                          />
                                        )}
                                        <span className="truncate">
                                          {typeof option === 'string' && showIcons ? (
                                            <div className="flex items-center gap-x-2">
                                              <DynamicIcon size={20} iconName={option} />
                                              {optionLabel}
                                            </div>
                                          ) : (
                                            optionLabel
                                          )}
                                        </span>
                                      </CommandItem>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-foreground text-xs text-background">
                                    <p>{optionLabel}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )
                          })}
                        </div>
                      </div>
                    </CommandGroup>
                  </CommandList>
                  {selectedValue && (
                    <div className="flex justify-end gap-x-2 border-t p-2">
                      <Button size="sm" variant="outline" className="h-6" onClick={() => formField.onChange('')}>
                        <IconCloseOutline className="mr-2 size-3" />
                        Clear
                      </Button>
                      {applyButton}
                    </div>
                  )}
                </Command>
              </PopoverContent>
            </Popover>
            <FormDescription>{field.description}</FormDescription>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
